import { isAuthLoaded } from "@/lib/authState";
import { auth, updateProfile, storage, updateEmail, EmailAuthProvider, reauthenticateWithCredential, sendEmailVerification, db } from "@/lib/firebase";
import { toast } from "sonner";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from 'uuid';
import { getValidToken } from "@/js/external-js/variableModules/utils/getValidToken.util";
import { doc, getDoc, setDoc } from "firebase/firestore";

const MAX_PHOTO_URL_LENGTH = 2000; // Firebase's limit is 2048, keeping it conservative

// Validate photo URL before sending to Firebase
const validatePhotoUrl = (url: string): boolean => {
  if (!url) return true; // Empty URL is valid (removes photo)
  if (url.length > MAX_PHOTO_URL_LENGTH) {
    console.error(`[UserService] Photo URL exceeds maximum length of ${MAX_PHOTO_URL_LENGTH} characters`);
    toast.error('Profile picture URL is too long. Please use a shorter URL or a different image.');
    return false;
  }
  return true;
};

export const getUserInfo = async () => {
  if (isAuthLoaded()) {
    const user = auth.currentUser;
    if (!user) {
      console.error('[UserService] No current user found, cannot get user info');
      return null;
    }
    // fetch users/{userId}
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    const res = {
        ...user,
        ...userDoc.data()
    }
    return res;
  }
  console.error('[UserService] Auth not loaded, cannot get user info');
  return null;
}

/**
 * Uploads a file to Firebase Storage and returns the download URL
 */
const uploadFile = async (file: File, path: string): Promise<string> => {
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

/**
 * Uploads a profile picture to Firebase Storage
 * @param file The image file to upload
 * @param userId The user's UID
 * @returns The download URL of the uploaded image
 */
export const uploadProfilePicture = async (file: File, userId: string): Promise<string> => {
  if (!file || !userId) {
    throw new Error('File and user ID are required');
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg', 'image/gif'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Only JPEG, PNG, JPG, GIF, and WebP images are allowed');
  }

  // Validate file size (max 2MB)
  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    throw new Error('Image must be smaller than 2MB');
  }

  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `profile.${fileExt || 'jpg'}`;
    const filePath = `users/${userId}/${fileName}`;
    
    return await uploadFile(file, filePath);
  } catch (error) {
    console.error('Error uploading profile picture:', error);
    throw new Error('Failed to upload profile picture');
  }
};

export interface DashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  idx: number;
}
export type DashboardLayout = DashboardLayoutItem[];

export interface TableColumnConfig {
  columnOrder?: string[];
  columnVisibility?: Record<string, boolean>;
  customColumnKeys?: string[];
}

export type TableColumnConfigMap = Record<string, TableColumnConfig>;

export interface UserProfileUpdates {
  displayName?: string;
  photoURL?: string | File | null;
  email?: string;
  currentPassword?: string; // Required for email changes
  theme?: {
    mode?: 'light' | 'dark' | 'system';
    primaryColor?: string;
    accentColor?: string;
  };
  dashboardConfig?: DashboardLayout;
  mobileDashboardConfig?: DashboardLayout;
  // --- Sidebar persistence fields ---
  sidebarWidth?: number;
  sidebarCollapsed?: boolean;
  // --- Table preferences ---
  rowsPerPage?: number;
  tableColumnConfigs?: TableColumnConfigMap;
  mcmDirectMode?: boolean;
  // --- Onboarding pill (user-local UI prefs) ---
  onboardingPillPosition?: { x: number; y: number };
  onboardingPillCollapsed?: boolean;
  onboardingPillPanelCollapsed?: boolean;
  // --- Onboarding celebration (shown once per user) ---
  onboardingCelebrationShown?: boolean;
}

/**
 * Updates a user's email address with re-authentication
 * @param newEmail The new email address
 * @param currentPassword The user's current password for re-authentication
 * @returns Promise that resolves when email is updated
 */
export const updateUserEmail = async (newEmail: string, currentPassword: string): Promise<void> => {
  if (!isAuthLoaded() || !auth.currentUser) {
    throw new Error('User not authenticated');
  }

  const user = auth.currentUser;
  
  // Validate email format
  if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
    throw new Error('Please enter a valid email address');
  }

  // Don't do anything if email hasn't changed
  if (user.email === newEmail) {
    return;
  }

  try {
    // First, call the internal API to update the email
    const idToken = await user.getIdToken();
    const response = await fetch(`${window.apiURL}/updateUserEmail`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
        'Company-ID': window.companyID || ''
      },
      body: JSON.stringify({
        newEmail,
        userID: user.uid,
        currentPassword
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to update email');
    }
    
    // If API call is successful, update Firebase auth email
    const credential = EmailAuthProvider.credential(user.email || '', currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update email in Firebase Auth
    await updateEmail(user, newEmail);
    
    // Send verification email to the new address
    await sendEmailVerification(user);
    
    // Refresh the token to ensure it has the latest email
    await user.getIdToken(true);
    
    toast.success('Email updated successfully. Please check your new email for a verification link.');
  } catch (error) {
    console.error('Error updating email:', error);
    
    let errorMessage = 'Failed to update email';
    
    if (error instanceof Error) {
      switch (error.message) {
        case 'Firebase: Error (auth/requires-recent-login).':
          errorMessage = 'Please log in again to update your email';
          break;
        case 'Firebase: Error (auth/wrong-password).':
          errorMessage = 'Incorrect password. Please try again.';
          break;
        case 'Firebase: Error (auth/email-already-in-use).':
          errorMessage = 'This email is already in use by another account';
          break;
        case 'Firebase: Error (auth/invalid-email).':
          errorMessage = 'Please enter a valid email address';
          break;
        default:
          errorMessage = error.message;
      }
    }
    
    throw new Error(errorMessage);
  }
};

export const saveUserInfo = async (updates: UserProfileUpdates) => {
  if (!isAuthLoaded()) {
    console.error('[UserService] Auth not loaded, cannot save user info');
    return null;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    console.error('[UserService] No current user found, cannot save user info');
    return null;
  }

  try {
    // Handle email update first if needed (requires re-authentication)
    if (updates.email && updates.email !== currentUser.email) {
      if (!updates.currentPassword) {
        throw new Error('Current password is required to update email');
      }
      
      await updateUserEmail(updates.email, updates.currentPassword);
      // Don't include email in profile updates
      delete updates.email;
      delete updates.currentPassword;
    }

    // Filter out undefined values and create update object
    const updatesToApply = Object.entries(updates).reduce((acc, [key, value]) => {
      if (value !== undefined && key !== 'currentPassword') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    // Check if there are any changes to apply to profile
    const hasProfileChanges = Object.entries(updatesToApply).some(
      ([key, value]) => currentUser[key as keyof typeof currentUser] !== value
    );


    // Handle profile picture upload if a File object is provided
    if (updatesToApply.photoURL instanceof File) {
      try {
        const downloadURL = await uploadProfilePicture(updatesToApply.photoURL, currentUser.uid);
        updatesToApply.photoURL = downloadURL;
      } catch (error) {
        console.error('Error uploading profile picture:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload profile picture');
        return null;
      }
    } else if (updatesToApply.photoURL === null) {
      // If photoURL is explicitly set to null, it means remove the photo
      updatesToApply.photoURL = null;
    } else if (updatesToApply.photoURL && !validatePhotoUrl(updatesToApply.photoURL as string)) {
      return null;
    }

    // Handle profile updates
    const {
      displayName,
      photoURL,
      theme,
      dashboardConfig,
      mobileDashboardConfig,
      sidebarWidth,
      sidebarCollapsed,
      rowsPerPage,
      tableColumnConfigs,
      mcmDirectMode,
      onboardingPillPosition,
      onboardingPillCollapsed,
      onboardingPillPanelCollapsed,
      onboardingCelebrationShown,
      ...rest
    } = updatesToApply;
    const profileUpdates: { displayName?: string; photoURL?: string | null } = {};
    
    if (displayName !== undefined) profileUpdates.displayName = displayName;
    if (photoURL !== undefined) profileUpdates.photoURL = photoURL;

    // Prepare Firestore updates
    const firestoreUpdates: Record<string, any> = {};
    
    // Handle theme updates
    if (theme) {
      firestoreUpdates.theme = theme;
      console.log('[UserService] Updating theme preferences:', theme);
    }

    if (sidebarWidth !== undefined) {
      firestoreUpdates.sidebarWidth = sidebarWidth;
    }

    if (sidebarCollapsed !== undefined) {
      firestoreUpdates.sidebarCollapsed = sidebarCollapsed;
    }

    // Handle MCM preferences
    if (mcmDirectMode !== undefined) {
      firestoreUpdates.mcmDirectMode = mcmDirectMode;
    }

    // Handle onboarding pill prefs (user-local)
    if (onboardingPillPosition !== undefined) {
      firestoreUpdates.onboardingPillPosition = onboardingPillPosition;
    }
    if (onboardingPillCollapsed !== undefined) {
      firestoreUpdates.onboardingPillCollapsed = onboardingPillCollapsed;
    }
    if (onboardingPillPanelCollapsed !== undefined) {
      firestoreUpdates.onboardingPillPanelCollapsed = onboardingPillPanelCollapsed;
    }

    // Handle onboarding celebration shown (once per user)
    if (onboardingCelebrationShown !== undefined) {
      firestoreUpdates.onboardingCelebrationShown = onboardingCelebrationShown;
    }

    // Handle dashboardConfig updates for Firestore
    if (dashboardConfig !== undefined) {
      const cleanDashboardConfig = Array.isArray(dashboardConfig)
        ? dashboardConfig.map(item => {
            if (typeof item === 'object' && item !== null) {
              const cleaned: Record<string, any> = {};
              for (const key in item) {
                if (item[key] === undefined || item[key] === null) continue;
                cleaned[key] = item[key];
              }
              return cleaned;
            }
            return item;
          })
        : dashboardConfig;
      firestoreUpdates.dashboardConfig = cleanDashboardConfig;
      console.log('[UserService] Updating dashboardConfig:', cleanDashboardConfig);
    }

    if (mobileDashboardConfig !== undefined) {
      const cleanMobileDashboardConfig = Array.isArray(mobileDashboardConfig)
        ? mobileDashboardConfig.map(item => {
            if (typeof item === 'object' && item !== null) {
              const cleaned: Record<string, any> = {};
              for (const key in item) {
                if (item[key] === undefined || item[key] === null) continue;
                cleaned[key] = item[key];
              }
              return cleaned;
            }
            return item;
          })
        : mobileDashboardConfig;
      firestoreUpdates.mobileDashboardConfig = cleanMobileDashboardConfig;
      console.log('[UserService] Updating mobileDashboardConfig:', cleanMobileDashboardConfig);
    }

    // Handle rowsPerPage updates for Firestore
    if (rowsPerPage !== undefined) {
      firestoreUpdates.rowsPerPage = rowsPerPage;
    }

    if (tableColumnConfigs !== undefined) {
      firestoreUpdates.tableColumnConfigs = tableColumnConfigs;
    }

    // Handle name updates for Firestore
    if (displayName !== undefined) {
      const firstName = displayName.split(' ')[0];
      const lastName = displayName.split(' ').slice(1).join(' ');
      firestoreUpdates.firstName = firstName;
      firestoreUpdates.lastName = lastName;
      firestoreUpdates.displayName = displayName;
    }

    // Handle photo URL updates for Firestore
    if (photoURL !== undefined) {
      firestoreUpdates.photoURL = photoURL;
    }

    // Update Firebase Auth profile if needed
    if (Object.keys(profileUpdates).length > 0) {
      console.log('[UserService] Updating Auth profile:', profileUpdates);
      await updateProfile(currentUser, profileUpdates);
    }

    // Update Firestore if we have any updates
    if (Object.keys(firestoreUpdates).length > 0) {
      console.log('[UserService] Updating Firestore user document:', firestoreUpdates);
      const userRef = doc(db, 'users', currentUser.uid);
      await setDoc(userRef, firestoreUpdates, { merge: true });
    }

    if (Object.keys(profileUpdates).length > 0 || Object.keys(firestoreUpdates).length > 0) {
      toast.success('Profile updated successfully');
    }

    return { ...currentUser, ...updatesToApply };
  } catch (error) {
    console.error('[UserService] Error updating user profile:', error);
    toast.error('Failed to update profile. Please try again.');
    return null;
  }
};

export const saveTableColumnConfig = async (
  tableId: string,
  config: TableColumnConfig
) => {
  if (!isAuthLoaded() || !auth.currentUser) {
    console.error('[UserService] Auth not loaded, cannot save table column config');
    return;
  }

  try {
    const user = auth.currentUser;
    const userRef = doc(db, 'users', user.uid);
    await setDoc(
      userRef,
      { tableColumnConfigs: { [tableId]: config } },
      { merge: true }
    );
  } catch (error) {
    console.error('[UserService] Failed saving table column config:', error);
    throw error;
  }
};

export default { getUserInfo, saveUserInfo };